import styles from './Modal.module.css';
import closeIcon from 'assets/svg/close-01.svg';

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import RegularButton from '../RegularButton';
import IconButton from '../IconButton';

/**
 * Компонент `Modal` представляет модальное окно для отображения информации
 * или выполнения действий. Позволяет настраивать заголовок, содержимое и футер.
 *
 * @component
 *
 * @param {Object} props - Свойства компонента
 * @param {string} props.title - Заголовок модального окна.
 * @param {boolean} [props.open=true] - Определяет, открыто ли модальное окно.
 * @param {function} props.onSubmit - Обработчик нажатия кнопки "Сохранить" в дефолтном футере.
 * @param {function} props.onClose - Обработчик закрытия модального окна.
 * @param {React.ReactNode} props.children - Содержимое модального окна (может включать `Modal.Footer`).
 * @param {boolean} [props.hideDefaultFooter=false] - Скрывает дефолтный футер.
 * @param {string} [props.submitText='Сохранить'] - Текст кнопки "Сохранить" в дефолтном футере.
 * @param {string} [props.cancelText='Отмена'] - Текст кнопки "Отмена" в дефолтном футере.
 * @param {string} [props.className=undefined] - Добавляет класс Модалке.
 * @param {boolean} [props.disabledSubmit] - Запретить нажатие кнопки "Сохранить" .
 *
 * @returns {React.ReactPortal|null} Возвращает модальное окно в виде портала, если оно открыто.
 */
const Modal = ({
  title,
  open = true,
  onSubmit = undefined,
  onClose = undefined,
  children,
  hideDefaultFooter = false,
  submitText = 'Сохранить',
  cancelText = 'Отмена',
  className = undefined,
  disabledSubmit = false,
}) => {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [open]);

  if (!open) return null;

  const getFooter = () => {
    const footer = React.Children.toArray(children).find(
      (child) => React.isValidElement(child) && child.type === Modal.Footer,
    );

    if (footer) return <div id="custom-modal-footer">{footer}</div>;

    if (!hideDefaultFooter) {
      return (
        <Modal.Footer
          onSubmit={onSubmit}
          onCancel={onClose}
          submitText={submitText}
          cancelText={cancelText}
          disabledSubmit={disabledSubmit}
        />
      );
    }

    return null;
  };

  const content = React.Children.toArray(children).filter(
    (child) => !(React.isValidElement(child) && child.type === Modal.Footer),
  );

  return ReactDOM.createPortal(
    <div className={styles.container}>
      <div className={classNames(styles.modal, className)}>
        <ModalHeader text={title} onClose={onClose} />
        <ModalBody open={open}>{content}</ModalBody>
        <div className={styles.divider}></div>
        {getFooter()}
      </div>
    </div>,
    document.body,
  );
};

/**
 * Компонент `ModalHeader` предназначен для отображения заголовка модального окна,
 * включая кнопку закрытия.
 *
 * @param {Object} props - Свойства компонента
 * @param {string} props.text - Текст заголовка.
 * @param {function} props.onClose - Обработчик закрытия модального окна.
 *
 * @returns {JSX.Element} Заголовок модального окна.
 */
const ModalHeader = ({ text, onClose }) => (
  <div className={classNames(styles.header)} id="modal-header">
    <span className={styles.title}>{text}</span>
    <IconButton
      tooltip="Закрыть"
      color="secondary"
      icon={closeIcon}
      size={10}
      variant="outlined"
      onClick={onClose}
    />
  </div>
);

/**
 * Компонент `ModalBody` предоставляет контейнер для основного содержимого модального окна.
 *
 * @param {Object} props - Свойства компонента
 * @param {React.ReactNode} props.children - Основное содержимое модального окна.
 * @param {Boolean} props.open - Отслеживание открытия модалки
 *
 * @returns {JSX.Element} Контейнер для содержимого модального окна.
 */
const ModalBody = ({ children, open }) => {
  const observerRef = useRef(null);
  const [layoutHeight, setLayoutHeight] = useState(0);

  useEffect(() => {
    if (!open) return;

    const layoutElements = ['custom-modal-footer', 'modal-header', 'modal-footer'];

    calculateLayoutHeight(layoutElements);

    observerRef.current = new ResizeObserver(() => calculateLayoutHeight(layoutElements));
    layoutElements.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [open]);

  const calculateLayoutHeight = (layoutElements) => {
    let height = 30;

    layoutElements.forEach((id) => {
      const el = document.querySelector(`#${id}`);
      if (el) {
        height += el.clientHeight;
      }
    });

    setLayoutHeight(height);
  };

  return <div className={classNames(styles.body)}>{children}</div>;
};

/**
 * Компонент `Modal.Footer` отображает футер модального окна.
 * По умолчанию содержит кнопки "Сохранить" и "Отмена", если не передан кастомный контент.
 *
 * @param {Object} props - Свойства компонента
 * @param {React.ReactNode} [props.children] - Кастомный футер (если задан, заменяет дефолтные кнопки).
 * @param {function} [props.onSubmit] - Обработчик кнопки "Сохранить".
 * @param {boolean} [props.disabledSubmit] - Запретить нажатие кнопки "Сохранить" .
 * @param {function} [props.onCancel] - Обработчик кнопки "Отмена".
 * @param {string} [props.submitText='Сохранить'] - Текст кнопки "Сохранить".
 * @param {string} [props.cancelText='Отмена'] - Текст кнопки "Отмена".
 *
 * @returns {JSX.Element} Футер модального окна.
 */
Modal.Footer = function ModalFooter({
  children,
  onSubmit = undefined,
  onCancel = undefined,
  submitText = 'Сохранить',
  cancelText = 'Отмена',
  disabledSubmit,
}) {
  return (
    <div className={classNames(styles.footer)} id="modal-footer">
      {children || (
        <>
          <RegularButton
            text={submitText}
            color="primary"
            variant="flat"
            onClick={onSubmit}
            disabled={disabledSubmit}
          />
          <RegularButton text={cancelText} color="primary" variant="outlined" onClick={onCancel} />
        </>
      )}
    </div>
  );
};

Modal.Footer.displayName = 'ModalFooter';

export default Modal;
